import {api} from "@/lib/api";

/**
 * One row of the inbox.
 *
 * Carries i18n KEYS, never rendered text: the backend writes a notification once and the app
 * re-renders it in whatever language the UI is currently set to, so a row written in German last
 * week reads in English today if the user switched. `titleKey`/`bodyKey` are resolved against
 * `assets/locales/*` with `params` as the interpolation values — see `notifications.chat.*` there.
 */
export interface NotificationItem {
    id: string;
    /** Doubles as the opt-out key, e.g. `chat.message`. */
    type: string;
    /** Narrows `type` to one subject — for chat, the channel id. Empty string when unscoped. */
    discriminator: string;
    titleKey: string;
    bodyKey: string;
    /**
     * Interpolation values for the two keys. Scalars only — anything else has no rendering, and
     * the only producer today (chat) emits strings.
     */
    params: Record<string, string | number>;
    /**
     * Routing payload for a tap, e.g. `{route: "chat", channelId, orgId}`. Deliberately untyped
     * here: T15 owns deep-link handling and is the only place that should interpret it.
     */
    data: Record<string, unknown>;
    read: boolean;
    /** PocketBase's `YYYY-MM-DD HH:MM:SS.sssZ`, as every other date in this app. */
    created: string;
    /**
     * When this notification last had real activity, and the field the inbox is SORTED by.
     *
     * The one to display, therefore: a collapsed row that gets bumped resurfaces at the top, and
     * showing `created` there dates it to when it was first written — "3 days ago" sitting above
     * "1 hour ago", with nothing on screen to explain the order. `created` is still sent and is
     * still the row's birth date; it is simply not what the list is ordered by.
     *
     * Never empty: the backend substitutes `created` for a row written before the column existed,
     * so this is a plain `string` and not an optional one.
     */
    lastEventAt: string;
}

export interface NotificationPage {
    items: NotificationItem[];
    page: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
    /**
     * The caller's unread total, for the whole inbox rather than this page. It rides along on
     * every list response so painting the list and the badge costs one request, not two.
     */
    unreadCount: number;
}

export interface ListNotificationsParams {
    page?: number;
    perPage?: number;
    unreadOnly?: boolean;
}

export interface MarkReadResult {
    success: boolean;
    /** Recounted server-side after the write, so it is authoritative — never recompute it. */
    unreadCount: number;
}

/** Hard limit the backend enforces on `POST /notifications/read`. */
export const MARK_READ_MAX_IDS = 200;

/**
 * One page of the caller's inbox, newest activity first.
 *
 * Scoping is structural: the endpoint takes no user id and always answers for the bearer token,
 * so there is no way to spell a request for somebody else's notifications.
 */
export async function listNotifications(params: ListNotificationsParams = {}): Promise<NotificationPage> {
    const xhr = await api.get<NotificationPage>("/notifications", {params});
    return xhr.data;
}

/**
 * The unread total on its own.
 *
 * For the places that want the badge without the list — the dashboard bell and the OS app-icon
 * badge — where paging the whole inbox to learn one integer would be wasteful.
 */
export async function getUnreadCount(): Promise<number> {
    const xhr = await api.get<{count: number}>("/notifications/unread-count");
    return xhr.data.count;
}

/**
 * Mark specific notifications read.
 *
 * Ids that are already read, unknown, or belong to another user are skipped rather than rejected,
 * so a stale client cannot be made to fail. Answers with the recounted unread total.
 */
export async function markNotificationsRead(ids: string[]): Promise<MarkReadResult> {
    const xhr = await api.post<MarkReadResult>("/notifications/read", {ids});
    return xhr.data;
}

/** Empty the badge in one call. `unreadCount` always comes back as 0. */
export async function markAllNotificationsRead(): Promise<MarkReadResult> {
    const xhr = await api.post<MarkReadResult>("/notifications/read-all");
    return xhr.data;
}

/** How the backend reaches a device. This app only ever registers as `expo`. */
export type PushTransport = "expo" | "web";

/** The device platforms the backend accepts — anything else is refused as `invalidPlatform`. */
export type PushDevicePlatform = "ios" | "android" | "web";

export interface RegisterDeviceRequest {
    token: string;
    transport: PushTransport;
    platform: PushDevicePlatform;
    /**
     * The full language tag the UI is currently using (`de`, `de-AT`, `pt-BR`, …). The backend
     * renders every push for this device in the locale stored on its row and normalizes the tag
     * itself, so the app passes what i18next has and never trims it down.
     */
    locale: string;
    /** Stable per-install id: the backend keeps a single row per (user, deviceId). */
    deviceId: string;
    appVersion?: string;
}

/**
 * Register or refresh this device's push token.
 *
 * Idempotent by contract — the backend reconciles both the globally unique `token` and the
 * (user, deviceId) pair — so the app calls it on every authenticated launch instead of trying to
 * remember whether it already did, and a token that moved to another account is re-pointed rather
 * than left pushing to the previous user.
 */
export async function registerDevice(request: RegisterDeviceRequest): Promise<{success: boolean}> {
    const xhr = await api.post<{success: boolean}>("/notifications/devices", request);
    return xhr.data;
}

/**
 * Stop the backend pushing to this token.
 *
 * POST rather than DELETE because Expo tokens (`ExponentPushToken[xxx]`) contain brackets and do not
 * survive a path segment. The endpoint answers `{success: true}` even for a token the caller does not
 * own, so the response is an acknowledgement, not a receipt that a row was deleted.
 */
export async function unregisterDevice(token: string): Promise<{success: boolean}> {
    const xhr = await api.post<{success: boolean}>("/notifications/devices/unregister", {token});
    return xhr.data;
}

/**
 * The opt-out key chat messages are delivered under, and the only `type` this app writes today.
 *
 * A constant rather than a literal at each call site because it is a wire value shared with the
 * backend: it appears in `NotificationItem.type`, in the settings rows, and in every resolve
 * query, and a typo in any one of them fails silently as "some other type, default enabled".
 */
export const CHAT_MESSAGE_NOTIFICATION_TYPE = "chat.message";

/**
 * One STORED opt-out row.
 *
 * The backend only stores a row when it differs from what resolution would produce without it, and
 * sweeps rows that stop differing. So the absence of a row says nothing about what the user will
 * receive, and this list is a description of the exceptions, not of the settings — read
 * `resolveNotificationSetting` for what actually governs delivery.
 */
export interface NotificationSetting {
    /** The notification type this row governs, e.g. `chat.message`. */
    type: string;
    /** Narrows `type` to one subject — for chat, the channel id. Empty string for the whole type. */
    discriminator: string;
    enabled: boolean;
}

/** What a given (type, discriminator) actually resolves to, after the backend's resolution order. */
export interface ResolvedNotificationSetting {
    type: string;
    discriminator: string;
    enabled: boolean;
}

export interface NotificationSettingWrite {
    type: string;
    /** Omit or pass `""` to write the type-level row. */
    discriminator?: string;
    enabled: boolean;
}

export interface NotificationSettingWriteResult {
    success: boolean;
    /**
     * The RESOLVED value after the write and after redundant rows were swept — not an echo of what
     * was sent. The two normally agree; when they do not, this is the one that will govern
     * delivery, so callers should trust it over their own optimism.
     */
    enabled: boolean;
}

/**
 * The caller's stored opt-out rows.
 *
 * Only the exceptions, for the reason on `NotificationSetting`. Nothing in this app needs it to
 * paint a switch — it is here because the endpoint exists and a settings screen that wants to show
 * "you have muted 3 channels" would need it.
 */
export async function listNotificationSettings(): Promise<NotificationSetting[]> {
    const xhr = await api.get<{settings: NotificationSetting[]}>("/notifications/settings");
    return xhr.data.settings;
}

/**
 * Ask what one (type, discriminator) resolves to.
 *
 * The endpoint exists precisely so the app never reimplements the resolution order (exact row →
 * type row → default true). Do not derive this from `listNotificationSettings`: the rule lives on
 * the backend, in one place, and the intentional "chat off, but not this channel" state is only
 * correct if both sides agree on it.
 */
export async function resolveNotificationSetting(
    type: string,
    discriminator = ""
): Promise<ResolvedNotificationSetting> {
    const xhr = await api.get<ResolvedNotificationSetting>("/notifications/settings/resolve", {
        params: {type, discriminator}
    });
    return xhr.data;
}

/**
 * Flip a switch.
 *
 * Whether that stores a row, changes one or removes one is the backend's call — turning a channel
 * back on under a type that is already on deletes the exception rather than storing a redundant
 * `true`. The caller states the intent and reads the resolved answer back.
 */
export async function setNotificationSetting(write: NotificationSettingWrite): Promise<NotificationSettingWriteResult> {
    const xhr = await api.put<NotificationSettingWriteResult>("/notifications/settings", write);
    return xhr.data;
}
